import React, { useEffect, useState, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    Container,
    Row,
    Col,
    Alert,
    Spinner,
    ListGroup,
    ListGroupItem,
    Badge,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import * as dynamicConfigService from "../../services/api/dynamic-config-template-service";
import SchemaBuilderCore from "./SchemaBuilderCore";
import UserContext from "../../UserContext";

function ReadOnlyTreeBranch({ node, depth = 0, expanded, toggleExpand }) {
    if (!node) return null;
    const isOpen = expanded[node.id] !== false;
    const polyVariants = node.nodeType === "polymorphicArray" && Array.isArray(node.variants) ? node.variants : [];
    const hasKids =
        (node.children && node.children.length) ||
        (node.nodeType === "array" && node.itemSchema) ||
        (node.nodeType === "polymorphicArray" && polyVariants.length > 0);
    return (
        <div style={{ marginLeft: depth * 14, marginTop: 4 }}>
            <div style={{ padding: "2px 4px", borderRadius: 4, whiteSpace: "nowrap" }}>
                {hasKids ? (
                    <span
                        onClick={() => toggleExpand(node.id)}
                        style={{ marginRight: 6, cursor: "pointer" }}
                    >
                        {isOpen ? "▼" : "▶"}
                    </span>
                ) : (
                    <span style={{ marginRight: 18 }} />
                )}
                <strong>{node.key || "?"}</strong>{" "}
                <span style={{ color: "#666" }}>({node.nodeType})</span>
                {node.label ? ` — ${node.label}` : ""}
            </div>
            {hasKids && isOpen && node.nodeType === "array" && node.itemSchema && (
                <ReadOnlyTreeBranch node={node.itemSchema} depth={depth + 1} expanded={expanded} toggleExpand={toggleExpand} />
            )}
            {hasKids && isOpen && node.nodeType === "polymorphicArray" && polyVariants.map((variant) => (
                <div key={variant.id} style={{ marginLeft: 0 }}>
                    <div style={{ padding: "2px 4px", marginTop: 2, marginLeft: (depth + 1) * 14, borderRadius: 4, whiteSpace: "nowrap" }}>
                        <span style={{ color: "#666" }}>[variant]</span>{" "}
                        <strong>{String(variant.discriminatorValue)}</strong>
                        {variant.label ? ` — ${variant.label}` : ""}
                    </div>
                    {variant.itemSchema && (
                        <ReadOnlyTreeBranch node={variant.itemSchema} depth={depth + 2} expanded={expanded} toggleExpand={toggleExpand} />
                    )}
                </div>
            ))}
            {hasKids && isOpen && node.nodeType === "object" && (node.children || []).map((ch) => (
                <ReadOnlyTreeBranch key={ch.id} node={ch} depth={depth + 1} expanded={expanded} toggleExpand={toggleExpand} />
            ))}
        </div>
    );
}

export function SchemaTree({ schema }) {
    const [expanded, setExpanded] = useState({});
    const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: e[id] === false ? true : false }));
    if (!schema?.root) return <span className="text-muted small">No schema.</span>;
    return (
        <div style={{ maxHeight: 400, overflowY: "auto", fontSize: "0.85em" }}>
            {(schema.root.children || []).map((node) => (
                <ReadOnlyTreeBranch key={node.id} node={node} depth={0} expanded={expanded} toggleExpand={toggleExpand} />
            ))}
        </div>
    );
}

function formatTimeLeft(deadline) {
    const ms = new Date(deadline).getTime() - Date.now();
    if (ms <= 0) return "time's up";
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    return `${hours}h ${minutes}m left`;
}

const DynamicProjectInstanceBuilder = () => {
    const { instanceId } = useParams();
    const history = useHistory();
    const context = useContext(UserContext);
    const [bundle, setBundle] = useState(null);
    const [schema, setSchema] = useState(null);
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [alert, setAlert] = useState(null);
    const [rebaseVer, setRebaseVer] = useState("");

    // Version modal state
    const [versionModal, setVersionModal] = useState(null); // { version, schema }
    const [versionModalLoading, setVersionModalLoading] = useState(false);
    const [versionPreviewLink, setVersionPreviewLink] = useState("");
    const [savingVersionPreviewLink, setSavingVersionPreviewLink] = useState(false);
    const [versionModalAlert, setVersionModalAlert] = useState(null);

    const loadAll = async () => {
        setLoading(true);
        const [draftRes, verRes] = await Promise.all([
            dynamicConfigService.getProjectInstanceDraft(instanceId),
            dynamicConfigService.listProjectInstanceVersions(instanceId),
        ]);
        if (draftRes.status && draftRes.data?.instance) {
            setBundle(draftRes.data);
            const d = draftRes.data.draft;
            if (d?.schema) {
                setSchema(d.schema);
                setSelectedNodeId(d.schema.root?.id || null);
            }
        } else if (!draftRes.status && draftRes.error?.errorCode === 403) {
            context.setIsAuthorized(context);
        }
        if (verRes.status && verRes.data) setVersions(verRes.data);
        setLoading(false);
    };

    useEffect(() => {
        loadAll();
    }, [instanceId]);

    // Walk every node in the schema tree and collect validation errors
    const validateSchema = (node, errors = [], isRoot = false) => {
        if (!node) return errors;
        // Key must be non-empty for all non-root nodes
        if (!isRoot && !(node.key || "").trim()) {
            errors.push(`Node "${node.label || node.id}" has an empty key.`);
        }
        // Select: must have at least one option
        if (node.nodeType === "select" && !node.optionsSource && (!Array.isArray(node.options) || node.options.length === 0)) {
            errors.push(`Select field "${node.key || node.label}" has no options.`);
        }
        // PolymorphicArray: discriminatorKey must be set
        if (node.nodeType === "polymorphicArray") {
            if (!(node.discriminatorKey || "").trim()) {
                errors.push(`PolymorphicArray "${node.key || node.label}" is missing a discriminator key.`);
            }
            (node.variants || []).forEach((v, i) => {
                if (String(v.discriminatorValue ?? "").trim() === "") {
                    errors.push(`Variant ${i + 1} of "${node.key || node.label}" has an empty discriminator value.`);
                }
                validateSchema(v.itemSchema, errors, false);
            });
        }
        // Recurse into children
        if (Array.isArray(node.children)) {
            node.children.forEach((ch) => validateSchema(ch, errors, false));
        }
        // Recurse into array itemSchema
        if (node.itemSchema) {
            validateSchema(node.itemSchema, errors, false);
        }
        return errors;
    };

    const save = async () => {
        if (!schema) return;
        // Template name check
        if (!(schema.templateName || "").trim()) {
            setAlert({ type: "danger", msg: "Template name is required before saving." });
            return;
        }
        // Node-level validation
        const errors = validateSchema(schema.root, [], true);
        if (errors.length > 0) {
            setAlert({ type: "danger", msg: `Cannot save — fix these issues first:\n• ${errors.join("\n• ")}` });
            return;
        }
        setSaving(true);
        setAlert(null);
        const res = await dynamicConfigService.saveProjectInstanceDraft(instanceId, schema);
        setSaving(false);
        if (res.status) {
            setAlert({ type: "success", msg: "Project draft saved." });
            if (res.data?.draft?.schema) setSchema(res.data.draft.schema);
        } else {
            setAlert({ type: "danger", msg: res.error?.message || "Save failed" });
        }
    };

    const publish = async () => {
        setSaving(true);
        setAlert(null);
        const res = await dynamicConfigService.publishProjectInstance(instanceId);
        setSaving(false);
        if (res.status) {
            setAlert({ type: "success", msg: `Published project v${res.data.publishedProjectVersion}.` });
            await loadAll();
        } else {
            setAlert({ type: "danger", msg: res.error?.message || "Publish failed" });
        }
    };

    const rollback = async (toProjectVersion) => {
        if (!window.confirm(`Rollback (append-only) to project v${toProjectVersion}?`)) return;
        setSaving(true);
        const res = await dynamicConfigService.rollbackProjectInstance(instanceId, toProjectVersion);
        setSaving(false);
        if (res.status) {
            setAlert({ type: "success", msg: `New head v${res.data.newPublishedProjectVersion} (restored from v${toProjectVersion}).` });
            await loadAll();
        } else {
            setAlert({ type: "danger", msg: res.error?.message || "Rollback failed" });
        }
    };

    const unpublish = async (v) => {
        const confirmed = window.confirm(
            `Unpublish project v${v.projectVersion} back to draft?\n\n` +
            `Apps will get the previous published version until you publish again, ` +
            `and any changes in the current draft will be thrown away.`
        );
        if (!confirmed) return;
        setSaving(true);
        setAlert(null);
        const res = await dynamicConfigService.unpublishProjectInstance(instanceId, v.projectVersion);
        setSaving(false);
        if (res.status) {
            const live = res.data.currentProjectVersion
                ? `Apps now get v${res.data.currentProjectVersion}.`
                : "No published version is live now.";
            setAlert({ type: "success", msg: `v${res.data.unpublishedProjectVersion} is a draft again. ${live}` });
            await loadAll();
        } else {
            setAlert({ type: "danger", msg: res.error?.message || "Unpublish failed" });
        }
    };

    const rebaseDraft = async () => {
        if (!rebaseVer) return;
        setSaving(true);
        const res = await dynamicConfigService.rebaseProjectInstanceDraft(instanceId, Number(rebaseVer));
        setSaving(false);
        if (res.status) {
            setAlert({ type: "info", msg: "Draft replaced from base version. Publish to record rebase." });
            if (res.data?.draft?.schema) setSchema(res.data.draft.schema);
        } else {
            setAlert({ type: "danger", msg: res.error?.message || "Rebase failed" });
        }
    };

    const openVersionModal = async (v) => {
        if (v.status !== "published") return;
        setVersionModalLoading(true);
        setVersionModal({ version: v, schema: null });
        setVersionPreviewLink(String(v.previewLink || "").trim());
        setVersionModalAlert(null);
        const res = await dynamicConfigService.getProjectInstanceVersion(instanceId, v.projectVersion);
        setVersionModalLoading(false);
        if (res.status && res.data) {
            setVersionModal({ version: v, schema: res.data.schema || null });
        } else {
            setVersionModal({ version: v, schema: null });
        }
    };

    const closeVersionModal = () => {
        setVersionModal(null);
        setVersionPreviewLink("");
        setVersionModalAlert(null);
    };

    const saveVersionPreviewLink = async () => {
        if (!versionModal) return;
        setSavingVersionPreviewLink(true);
        setVersionModalAlert(null);
        const res = await dynamicConfigService.saveProjectVersionPreviewLink(
            instanceId,
            versionModal.version.projectVersion,
            versionPreviewLink.trim()
        );
        setSavingVersionPreviewLink(false);
        if (res.status && res.data?.version) {
            const updatedPreviewLink = String(res.data.version.previewLink || "").trim();
            setVersionPreviewLink(updatedPreviewLink);
            setVersionModal((prev) => prev ? { ...prev, version: { ...prev.version, previewLink: updatedPreviewLink } } : prev);
            setVersions((prev) =>
                prev.map((v) =>
                    v.projectVersion === versionModal.version.projectVersion
                        ? { ...v, previewLink: updatedPreviewLink }
                        : v
                )
            );
            setVersionModalAlert({ type: "success", msg: "Preview link saved." });
            loadAll();
        } else {
            setVersionModalAlert({ type: "danger", msg: res.error?.message || "Saving preview link failed" });
        }
    };

    if (loading || !schema) {
        return (
            <Container className="mt-4">
                <Spinner />
            </Container>
        );
    }

    const inst = bundle?.instance;
    const baseMeta = bundle?.baseMeta;

    return (
        <Container fluid className="mt-4">
            <Row className="mb-3">
                <Col>
                    <Button outline size="sm" onClick={() => history.push("/template/dynamic-config-project-assignments")}>
                        ← Project instances
                    </Button>
                    <h4 className="d-inline ml-3">Project instance</h4>
                    <Badge className="ml-2" color="secondary">
                        project {inst?.projectId}
                    </Badge>
                    <Badge className="ml-1" color="primary">
                        base: {baseMeta?.name} (forked v{inst?.createdFromBaseVersion})
                    </Badge>
                    <Badge className="ml-1" color="info">
                        project pv {inst?.currentProjectVersion}
                    </Badge>
                </Col>
            </Row>
            {alert && (
                <Alert color={alert.type} style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    <span>{alert.msg}</span>
                    <button
                        type="button"
                        onClick={() => setAlert(null)}
                        style={{
                            background: "rgba(0,0,0,0.2)",
                            border: "none",
                            borderRadius: 4,
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: "1rem",
                            lineHeight: 1,
                            padding: "2px 7px",
                            marginLeft: 12,
                            cursor: "pointer",
                            flexShrink: 0,
                        }}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </Alert>
            )}
            <Row>
                <Col md="3">
                    <Card>
                        <CardHeader>Project version history</CardHeader>
                        <CardBody style={{ maxHeight: 360, overflow: "auto", padding: "0.5rem" }}>
                            <ListGroup flush>
                                {versions.map((v) => (
                                    <ListGroupItem
                                        key={`${v.projectVersion}-${v.status}`}
                                        style={{
                                            cursor: v.status === "published" ? "pointer" : "default",
                                            padding: "8px 10px",
                                            borderLeft: v.projectVersion === inst?.currentProjectVersion && v.status === "published"
                                                ? "3px solid #28a745"
                                                : "3px solid transparent",
                                        }}
                                        onClick={v.status === "published" ? () => openVersionModal(v) : undefined}
                                        action={v.status === "published"}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <span style={{ fontWeight: 600, fontSize: "0.9em" }}>pv{v.projectVersion}</span>
                                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                                <Badge color={v.status === "draft" ? "warning" : "success"} style={{ fontSize: "0.7em" }}>
                                                    {v.status}
                                                </Badge>
                                                {v.previewLink && v.status === "published" && (
                                                    <Badge pill color="info" style={{ fontSize: "0.65em" }}>QR ✓</Badge>
                                                )}
                                            </div>
                                        </div>
                                        {v.derivedFromKind && (
                                            <div style={{ fontSize: "0.72em", color: "#888", marginTop: 2 }}>
                                                {v.derivedFromKind.replace(/_/g, " ")}
                                                {v.restoredFromProjectVersion != null && ` ← pv${v.restoredFromProjectVersion}`}
                                                {v.rebasedFromBaseVersion != null && ` · base v${v.rebasedFromBaseVersion}`}
                                            </div>
                                        )}
                                        {v.status === "published" && v.projectVersion !== inst?.currentProjectVersion && (
                                            <Button
                                                size="sm"
                                                color="link"
                                                className="p-0 mt-1"
                                                style={{ fontSize: "0.75em" }}
                                                onClick={(e) => { e.stopPropagation(); rollback(v.projectVersion); }}
                                                disabled={saving}
                                            >
                                                ↩ Restore snapshot
                                            </Button>
                                        )}
                                        {v.canUnpublish && (
                                            <Button
                                                size="sm"
                                                color="link"
                                                className="p-0 mt-1 text-danger"
                                                style={{ fontSize: "0.75em" }}
                                                onClick={(e) => { e.stopPropagation(); unpublish(v); }}
                                                disabled={saving}
                                            >
                                                Unpublish to draft ({formatTimeLeft(v.unpublishDeadline)})
                                            </Button>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </CardBody>
                    </Card>
                    <Card className="mt-3">
                        <CardHeader>Rebase draft from base</CardHeader>
                        <CardBody>
                            <FormGroup>
                                <Label>Published base version</Label>
                                <Input
                                    type="number"
                                    min={1}
                                    value={rebaseVer}
                                    onChange={(e) => setRebaseVer(e.target.value)}
                                    placeholder={`e.g. ${baseMeta?.currentVersion || 1}`}
                                />
                            </FormGroup>
                            <Button size="sm" color="warning" outline onClick={rebaseDraft} disabled={saving || !rebaseVer}>
                                Replace draft from base
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
                <Col md="9">
                    <Card className="mb-3">
                        <CardBody>
                            <Button color="primary" className="mr-2" onClick={save} disabled={saving}>
                                Save project draft
                            </Button>
                            <Button color="success" onClick={publish} disabled={saving}>
                                Publish project version
                            </Button>
                        </CardBody>
                    </Card>
                    <SchemaBuilderCore
                        schema={schema}
                        setSchema={setSchema}
                        selectedNodeId={selectedNodeId}
                        setSelectedNodeId={setSelectedNodeId}
                        expanded={expanded}
                        setExpanded={setExpanded}
                    />
                </Col>
            </Row>

            {/* Version view modal */}
            <Modal isOpen={!!versionModal} toggle={closeVersionModal} size="lg">
                <ModalHeader style={{ display: "flex", alignItems: "center" }}>
                    <span>
                        Version pv{versionModal?.version?.projectVersion}
                        {versionModal?.version?.derivedFromKind && (
                            <Badge pill color="secondary" className="ml-2" style={{ fontSize: "0.75em", verticalAlign: "middle" }}>
                                {versionModal.version.derivedFromKind.replace(/_/g, " ").toUpperCase()}
                            </Badge>
                        )}
                    </span>
                    <button
                        type="button"
                        className="close"
                        onClick={closeVersionModal}
                        style={{ position: "absolute", right: "1rem", top: "1rem" }}
                        aria-label="Close"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </ModalHeader>
                <ModalBody>
                    {versionModalAlert && (
                        <Alert color={versionModalAlert.type} toggle={() => setVersionModalAlert(null)}>
                            {versionModalAlert.msg}
                        </Alert>
                    )}
                    <Card className="mb-3">
                        <CardHeader>Preview URL (pub console QR)</CardHeader>
                        <CardBody>
                            <FormGroup className="mb-2">
                                <Label className="small text-muted mb-1">HTTPS preview link publishers can scan</Label>
                                <Input
                                    type="url"
                                    value={versionPreviewLink}
                                    onChange={(e) => setVersionPreviewLink(e.target.value)}
                                    placeholder="https://example.com/preview-path"
                                    autoComplete="off"
                                />
                            </FormGroup>
                            <Button
                                size="sm"
                                color="secondary"
                                outline
                                onClick={saveVersionPreviewLink}
                                disabled={savingVersionPreviewLink}
                            >
                                {savingVersionPreviewLink ? "Saving…" : "Save preview link"}
                            </Button>
                        </CardBody>
                    </Card>
                    <Card>
                        <CardHeader>Template schema (read-only)</CardHeader>
                        <CardBody>
                            {versionModalLoading ? (
                                <Spinner size="sm" />
                            ) : versionModal?.schema ? (
                                <SchemaTree schema={versionModal.schema} />
                            ) : (
                                <span className="text-muted">Schema not available.</span>
                            )}
                        </CardBody>
                    </Card>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={closeVersionModal}>Close</Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default DynamicProjectInstanceBuilder;
